import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { UserPlus, X, Shield, Users, Lock } from 'lucide-react';
import api from '../lib/api';
import { formatDateTime } from '../lib/constants';
import { useAuth } from '../lib/auth';
import { hasFeature, minPlanFor } from '../lib/plans';
import QuotaBanner from '../components/QuotaBanner';
import UpgradeRequestModal from '../components/UpgradeRequestModal';
import { useQuotaUsage } from '../lib/useQuotaUsage';

const ROLE_LABELS = {
  admin: 'Administrador (DPO)',
  compliance_officer: 'Compliance Officer',
  area_operativa: 'Área operativa',
  auditor_externo: 'Auditor Externo (solo lectura)',
};

export default function Team() {
  const { user, organization } = useAuth();
  const canInviteAuditor = hasFeature(organization, 'auditor_externo');
  const auditorMinPlan = minPlanFor('auditor_externo');
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: '', role: 'compliance_officer' });
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { usage, refresh: refreshUsage } = useQuotaUsage();

  const isAdmin = user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.get('/team/members'),
        isAdmin ? api.get('/team/invitations') : Promise.resolve({ data: [] }),
      ]);
      setMembers(m.data); setInvitations(i.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/team/invite', form);
      toast.success(data.email_sent ? `Invitación enviada a ${form.email}` : `Invitación creada (correo falló, comparte el link manualmente)`);
      setInviting(false); setForm({ email: '', role: 'compliance_officer' });
      await load();
      await refreshUsage();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'quota_exceeded') {
        toast.error(d.message || 'Alcanzó el tope de usuarios.');
        setInviting(false);
        setShowUpgrade(true);
      } else {
        toast.error(d || 'Error');
      }
    }
  };

  const cancel = async (id) => {
    if (!window.confirm('¿Cancelar esta invitación?')) return;
    await api.post(`/team/invitations/${id}/cancel`);
    toast.success('Invitación cancelada');
    await load();
  };

  const updateRole = async (userId, role) => {
    try {
      await api.patch(`/team/members/${userId}`, { role });
      toast.success('Rol actualizado');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const toggleActive = async (m) => {
    await api.patch(`/team/members/${m.id}`, { is_active: !m.is_active });
    toast.success(m.is_active ? 'Miembro desactivado' : 'Miembro reactivado');
    await load();
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Cargando equipo…</div>;

  return (
    <div className="space-y-8" data-testid="team-page">
      <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-5">
        <div>
          <div className="text-[15px] font-medium text-[#1C2329]">{members.length} miembro{members.length === 1 ? '' : 's'}</div>
          <div className="text-[13px] text-[#6B7280]">Gestione los roles y las invitaciones del equipo.</div>
        </div>
        {isAdmin && (
          <button className="pv-btn-primary flex items-center gap-2" onClick={() => setInviting(true)} data-testid="team-invite">
            <UserPlus size={14} /> Invitar miembro
          </button>
        )}
      </div>

      <QuotaBanner usage={usage} quotaKey="users" onRequestUpgrade={() => setShowUpgrade(true)} />
      <UpgradeRequestModal open={showUpgrade} onClose={(sent) => { setShowUpgrade(false); if (sent) refreshUsage(); }} />

      <section>
        <div className="pv-eyebrow mb-3">Miembros activos</div>
        <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
          {members.map((m) => (
            <div key={m.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`member-${m.id}`}>
              <div className="w-9 h-9 rounded-full bg-[#ECFDF5] border border-[#2A9B68]/30 flex items-center justify-center text-[#065F46] font-semibold text-[13px]">
                {(m.nombre?.[0] || '') + (m.apellido?.[0] || '')}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-medium text-[#1C2329]">{m.nombre} {m.apellido} {m.id === user?.id && <span className="text-[11px] text-[#6B7280] ml-1">(usted)</span>}</div>
                <div className="text-[12px] text-[#6B7280]">{m.email}</div>
              </div>
              {isAdmin && m.id !== user?.id ? (
                <select className="pv-input max-w-xs text-[13px]" value={m.role} onChange={(e) => updateRole(m.id, e.target.value)} data-testid={`role-${m.id}`}>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              ) : (
                <span className="pv-chip"><Shield size={11} /> {ROLE_LABELS[m.role] || m.role}</span>
              )}
              {isAdmin && m.id !== user?.id && (
                <button onClick={() => toggleActive(m)} className={`text-[12px] px-2 py-1 border ${m.is_active ? 'border-[#E5E7EB] text-[#6B7280]' : 'border-[#F59E0B]/50 text-[#92400E] bg-[#FFF7E6]'}`}>
                  {m.is_active ? 'Activar/Desactivar' : 'Reactivar'}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {isAdmin && invitations.length > 0 && (
        <section>
          <div className="pv-eyebrow mb-3">Invitaciones</div>
          <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
            {invitations.map((inv) => (
              <div key={inv.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`inv-${inv.id}`}>
                <Users size={14} className="text-[#6B7280]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-medium text-[#1C2329]">{inv.email}</div>
                  <div className="text-[12px] text-[#6B7280]">{ROLE_LABELS[inv.role]} · enviada {formatDateTime(inv.created_at)}</div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 border ${
                  inv.status === 'pendiente' ? 'border-[#F59E0B]/40 text-[#92400E] bg-[#FFF7E6]' :
                  inv.status === 'aceptada' ? 'border-[#2A9B68]/40 text-[#065F46] bg-[#ECFDF5]' :
                  'border-[#E5E7EB] text-[#6B7280]'
                }`}>
                  {inv.status}
                </span>
                {inv.status === 'pendiente' && (
                  <button onClick={() => cancel(inv.id)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1 rounded" title="Cancelar">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {inviting && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => setInviting(false)}>
          <form onSubmit={invite} className="bg-white border border-[#E5E7EB] max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="team-invite-modal">
            <div className="text-[15px] font-semibold text-[#1C2329] mb-4">Invitar miembro</div>
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Correo</label>
                <input required type="email" className="pv-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Rol</label>
                <select className="pv-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role">
                  <option value="admin">Administrador (DPO)</option>
                  <option value="compliance_officer">Compliance Officer</option>
                  <option value="area_operativa">Área operativa</option>
                  <option value="auditor_externo">Auditor Externo (solo lectura)</option>
                </select>
              </div>
              {form.role === 'auditor_externo' && (
                <div>
                  <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Duración del acceso (días)</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    className="pv-input"
                    value={form.access_days || 30}
                    onChange={(e) => setForm({ ...form, access_days: parseInt(e.target.value, 10) || 30 })}
                    data-testid="invite-access-days"
                  />
                  <div className="text-[11px] text-[#6B7280] mt-1">El acceso caduca automáticamente al vencer estos días.</div>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-between">
              <button type="button" className="pv-btn-secondary" onClick={() => setInviting(false)}>Cancelar</button>
              <button type="submit" className="pv-btn-primary" data-testid="invite-send">Enviar invitación</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
